import { useState } from "react";
import { useRecommendedList } from "@companion-core/shared/app/Queries/recommendedList";
import {
  buildRecommendedListSearchKey,
  getRecommendedListLoadingKeys,
  getRecommendedListRadiusValues,
  RecommendedListQueryArgs,
  shouldExpandRecommendedListRadius,
} from "../recommendedList.utils";
import { useIsScreenFocused } from "./useRecommendedList";

export const useRecommendedListSearch = (queryArgs: Omit<RecommendedListQueryArgs, "radius">) => {
  const radiusValues = getRecommendedListRadiusValues();
  const searchKey = buildRecommendedListSearchKey(queryArgs);
  const [search, setSearch] = useState({ key: searchKey, radiusIndex: 0 });
  const isFocused = useIsScreenFocused();
  const hasFrom = Boolean(queryArgs.from);
  // Filters changed: drop the radius reached by the previous search instead of inheriting it.
  const radiusIndex = search.key === searchKey ? search.radiusIndex : 0;
  if (search.key !== searchKey) {
    setSearch({ key: searchKey, radiusIndex });
  }
  const radius = radiusValues[Math.min(radiusIndex, radiusValues.length - 1)];
  const { data, isError, isSuccess, isPending, isLoading, isFetching } = useRecommendedList({
    ...queryArgs,
    ...(hasFrom ? { radius } : {}),
    enabled: isFocused,
  });
  const willExpand = shouldExpandRecommendedListRadius({
    hasFrom,
    radiusIndex,
    radiusCount: radiusValues.length,
    isError,
    isSuccess,
    resultCount: data?.length ?? 0,
  });
  if (willExpand) {
    setSearch({ key: searchKey, radiusIndex: radiusIndex + 1 });
  }

  const showLoadingState = isPending || isLoading || isFetching || willExpand;
  const loadingKeys = getRecommendedListLoadingKeys(radiusIndex > 0 || willExpand);

  return { data, showLoadingState, loadingKeys };
};
